import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import type { MonitoringCharts, MonitoringSeries } from '../api/devices'

/**
 * Graficele din Extended Monitoring (network / latency / disc IO), desenate
 * ca SVG cu tema implicită Highcharts 4.0.1 - aceleași culori de serie,
 * aceleași axe, fără jQuery.
 */

const COLORS = ['#7cb5ec', '#434348', '#90ed7d', '#f7a35c', '#8085e9', '#f15c80', '#e4d354', '#2b908f']
const FONT = '"Lucida Grande", "Lucida Sans Unicode", Verdana, Arial, Helvetica, sans-serif'

export function MonitoringChart({
  title,
  charts,
  kind,
  yTitle,
  pointUnit,
  boxStyle,
}: {
  title: string
  charts: MonitoringCharts
  kind: 'area' | 'spline'
  yTitle: string
  pointUnit: string
  boxStyle: string
}) {
  const names = Object.keys(charts.dates)
  if (names.length === 0) {
    return (
      <div className="alert alert-info">
        <strong>
          <i className="fas fa-info-circle"></i>
        </strong>{' '}
        Nothing to see yet...
      </div>
    )
  }
  return (
    <>
      {names.map((name, i) => (
        <ChartBox
          key={name}
          title={title.replace('{name}', name)}
          categories={charts.categories}
          series={charts.dates[name] ?? []}
          kind={kind}
          yTitle={yTitle}
          pointUnit={pointUnit}
          boxStyle={boxStyle}
          index={i}
        />
      ))}
    </>
  )
}

function ChartBox({
  title,
  categories,
  series,
  kind,
  yTitle,
  pointUnit,
  boxStyle,
}: {
  title: string
  categories: string[]
  series: MonitoringSeries[]
  kind: 'area' | 'spline'
  yTitle: string
  pointUnit: string
  boxStyle: string
  index: number
}) {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [hover, setHover] = useState<{ i: number; s: number } | null>(null)
  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const apply = () => setWidth(el.clientWidth)
    apply()
    if (typeof ResizeObserver === 'undefined') return
    const obs = new ResizeObserver(apply)
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  const height = 280
  const left = 54
  const right = 16
  const top = 36
  const bottom = 36
  const plotW = Math.max(1, width - left - right)
  const plotH = height - top - bottom
  const values = series.flatMap((s) => s.data)
  const dataMax = values.length ? Math.max(0, ...values) : 0
  const max = dataMax === 0 ? 1 : dataMax * 1.05
  const n = Math.max(1, categories.length - 1)
  const x = (i: number) => left + (i / n) * plotW
  const y = (v: number) => top + plotH - (v / max) * plotH
  const ticks = 4
  const tickVals = Array.from({ length: ticks + 1 }, (_, i) => Math.round((max / ticks) * i))

  return (
    <div ref={box} className="received" data-chart="chart" style={{ width: '100%', height: 280, ...parseBox(boxStyle) }}>
      {width > 0 && (
        <svg width={width} height={height} style={{ display: 'block' }}>
          <text x={width / 2} y={18} textAnchor="middle" fontFamily={FONT} fontSize={16} fill="#333">
            {title}
          </text>
          <text
            x={14}
            y={top + plotH / 2}
            textAnchor="middle"
            fontFamily={FONT}
            fontSize={11}
            fill="#606060"
            transform={`rotate(-90 14 ${top + plotH / 2})`}
          >
            {yTitle}
          </text>
          {tickVals.map((v) => (
            <g key={v}>
              <line x1={left} x2={left + plotW} y1={y(v)} y2={y(v)} stroke="#D8D8D8" />
              <text x={left - 6} y={y(v) + 4} textAnchor="end" fontFamily={FONT} fontSize={11} fill="#606060">
                {v}
              </text>
            </g>
          ))}
          <line x1={left} x2={left + plotW} y1={top + plotH} y2={top + plotH} stroke="#C0D0E0" />
          {categories.map((c, i) => {
            const step = Math.max(1, Math.ceil((categories.length - 1) / 15))
            if (i % step !== 0 && i !== categories.length - 1) return null
            return (
              <text key={c + i} x={x(i)} y={top + plotH + 16} textAnchor="middle" fontFamily={FONT} fontSize={11} fill="#606060">
                {c}
              </text>
            )
          })}
          {series.map((s, si) => {
            const color = COLORS[si % COLORS.length]
            const pts = s.data.map((v, i) => `${x(i)},${y(v)}`).join(' ')
            const area = `${x(0)},${top + plotH} ${pts} ${x(s.data.length - 1)},${top + plotH}`
            return (
              <g key={s.name}>
                {kind === 'area' && <polygon points={area} fill={color} fillOpacity={0.75} stroke="none" />}
                <polyline points={pts} fill="none" stroke={color} strokeWidth={2} />
              </g>
            )
          })}
          {hover && series[hover.s] && (
            <g>
              <circle cx={x(hover.i)} cy={y(series[hover.s]!.data[hover.i] ?? 0)} r={4} fill={COLORS[hover.s % COLORS.length]} />
              <rect x={x(hover.i) + 8} y={y(series[hover.s]!.data[hover.i] ?? 0) - 28} width={160} height={32} rx={2} fill="#fff" stroke="#aaa" />
              <text x={x(hover.i) + 16} y={y(series[hover.s]!.data[hover.i] ?? 0) - 16} fontFamily={FONT} fontSize={11} fill="#333">
                <tspan fontWeight={700}>
                  {series[hover.s]!.name} at {categories[hover.i]}
                </tspan>
              </text>
              <text x={x(hover.i) + 16} y={y(series[hover.s]!.data[hover.i] ?? 0) - 4} fontFamily={FONT} fontSize={11} fill="#333">
                {series[hover.s]!.data[hover.i]} {pointUnit}
              </text>
            </g>
          )}
          {series.map((s, si) => (
            <g key={'leg-' + s.name} onMouseEnter={() => undefined}>
              <rect x={left + si * 90} y={height - 14} width={8} height={8} fill={COLORS[si % COLORS.length]} />
              <text x={left + si * 90 + 12} y={height - 6} fontFamily={FONT} fontSize={11} fill="#333">
                {s.name}
              </text>
            </g>
          ))}
          {categories.map((_, i) =>
            series.map((s, si) => (
              <circle
                key={s.name + i}
                cx={x(i)}
                cy={y(s.data[i] ?? 0)}
                r={6}
                fill="transparent"
                onMouseEnter={() => setHover({ i, s: si })}
                onMouseLeave={() => setHover(null)}
              />
            )),
          )}
        </svg>
      )}
    </div>
  )
}

function parseBox(style: string): CSSProperties {
  if (style === 'network') {
    return { marginBottom: 16, border: '1px solid #ddd', boxShadow: '0px 0px 10px 0px #ddd' }
  }
  return { marginTop: 20, border: '1px solid #ddd', boxShadow: '5px 6px 3px 0 #ddd' }
}
